import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./popoverTooltip.html";
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { selectSharedLabels, WireI18n } from '~/modules/stores/i18nStore';
import { logger } from '~/modules/utilities/logger';

/**
 * A titled section for the plain, badge-free tooltip layout: a bold header
 * over its body text. Used by the v2 Social Impact tooltip, which wants the
 * feature name in bold above each description but none of the badge pills or
 * link buttons the `BadgeTooltip[]` layout renders.
 */

class Tooltip extends LightningElement {
  constructor(...args) {
    super(...args);
    // @ts-expect-error * Automated Type Error Fix
    this.tooltipData = null;
    // @ts-expect-error * Automated Type Error Fix
    this.popoverLayout = void 0;
    // @ts-expect-error * Automated Type Error Fix
    this.popoverOrientation = void 0;
    this.flipIfOverflowsViewport = false;
    /** Optional context-rich trigger label supplied by the owning section. */
    this.ariaLabel = '';
    // Optional plain layout: bold-titled sections, no badges or links. When set,
    // this takes precedence over `tooltipData`. Mutually exclusive with it in
    // practice — a consumer supplies one or the other.
    this.titledSections = void 0;
    this.defaultOrientation = {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.RIGHT
    };
    this.showPopover = false;
    this.ariaExpanded = 'false';
    this.labels = void 0;
  }
  get orientation() {
    return this.popoverOrientation || this.defaultOrientation;
  }
  get handleTooltipButtonRef() {
    return this.template.querySelector('.tooltip-button');
  }
  get ariaMoreInfo() {
    return this.ariaLabel || this.labels?.moreInfo || '';
  }

  /** Give the opened tooltip an accessible name even across the popover's shadow boundary. */
  get popoverAriaLabel() {
    const tooltipData = this.tooltipData;
    if (this.hasTitledSections) {
      return this.titledSections.map(section => `${section.title}: ${this.toPlainText(section.description)}`).join('. ');
    }
    if (typeof tooltipData === 'string') {
      return this.toPlainText(tooltipData);
    }
    if (Array.isArray(tooltipData)) {
      return tooltipData.map(item => {
        const badgeLabel = this.getBadgeLabel(item);
        const description = this.toPlainText(item.description);
        return badgeLabel && description ? `${badgeLabel}: ${description}` : badgeLabel || description;
      }).join('. ');
    }
    return this.ariaMoreInfo;
  }
  getBadgeLabel(item) {
    const badge = item?.badge;
    if (!badge) {
      return '';
    }
    const simpleBadgeValue = 'value' in badge && typeof badge.value === 'string' ? badge.value : '';
    try {
      return badge.i18nFn?.() ?? simpleBadgeValue;
    } catch {
      logger.warn(`Missing localized label for badge '${badge.i18nKey}'`);
      return simpleBadgeValue;
    }
  }
  get isTextTooltip() {
    return typeof this.tooltipData === 'string';
  }
  get hasTitledSections() {
    return Array.isArray(this.titledSections) && this.titledSections.length > 0;
  }
  get popoverLayoutStyle() {
    return this.popoverLayout ? `table-container ${this.popoverLayout}` : 'table-container';
  }
  handleTooltipButtonFocus() {
    this.handleTooltipButtonRef?.focus();
  }
  togglePopover() {
    if (this.showPopover === false) {
      this.openPopover();
    } else {
      this.closePopover();
    }
  }
  openPopover() {
    this.showPopover = true;
    this.ariaExpanded = 'true';
  }
  closePopover() {
    this.showPopover = false;
    this.ariaExpanded = 'false';
    this.handleTooltipButtonFocus();
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputKey(event) {
    if (event.key === 'Escape') {
      this.closePopover();
      event.preventDefault();
      event.stopPropagation();
    }
  }
  toPlainText(value) {
    return (value ?? '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\s+/g, ' ').trim();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Tooltip, {
  publicProps: {
    tooltipData: {
      config: 0
    },
    popoverLayout: {
      config: 0
    },
    popoverOrientation: {
      config: 0
    },
    flipIfOverflowsViewport: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    titledSections: {
      config: 0
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSharedLabels
        };
      }
    }
  },
  fields: ["defaultOrientation", "showPopover", "ariaExpanded"]
});
const __lwc_component_class_internal = _registerComponent(Tooltip, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;